import html2pdf from 'html2pdf.js/dist/html2pdf.min.js';
import { generateDocsReport } from './generateDocsReport';
import { uploadSummaryPdfToS3, vinForUpload } from './summaryUpload';

const hasResponse = (response) => response && Object.keys(response).length > 0;

export const docsPdfFileName = (response) =>
  response?.["TransactionID"] ? `${response["TransactionID"]}.pdf` : 'docsOutput.pdf';

export const docsPdfOptions = (response) => ({
  filename: docsPdfFileName(response),
  image: { type: 'jpeg', quality: 0.98 }, // <-- controls embedded image quality
  jsPDF: {
    unit: 'in',
    format: 'letter', // equivalent to [8.5, 11], just clearer
    orientation: 'portrait',
  },
  html2canvas: {
    scale: 2, // <-- default can render blurry text. 2 sharpens it
    useCORS: true, // if you load any external images
  },
  // 'avoid-all' used to be in this list. It tells html2pdf to treat *every*
  // element as unbreakable, so each nested wrapper that straddled a page
  // boundary got its own spacer div pushing it to the next page. Those
  // spacers compounded through the DOM tree and left the huge blank runs in
  // the Documents Output section. Instead we opt specific blocks in via the
  // 'avoid' selector below, so only the things we actually care about
  // keeping intact (each document card / link row) get pushed.
  // '.pdf-avoid-break .flex-row' mirrors the scoping the bot's print CSS
  // uses (DMV-bot-refi-docs/generate_docs_html.py). Protecting the
  // label/value rows *inside* an already-protected block stops a row being
  // sliced between its label and its value, which is the split the bot
  // handles more cleanly. It stays scoped to descendants of
  // .pdf-avoid-break rather than a bare '.flex-row' so it doesn't degrade
  // into the 'avoid-all' behaviour described above.
  pagebreak: {
    mode: ['css', 'legacy'],
    avoid: ['.pdf-avoid-break', '.pdf-avoid-break .flex-row'],
  },
});

// The docs summary PDF as a File, ready for the Download All zip or S3.
export const buildDocsSummaryPdf = async (docsFormData, docsOutput) => {
  const content = generateDocsReport(docsFormData, docsOutput);
  const blob = await html2pdf().set(docsPdfOptions(docsOutput)).from(content).outputPdf('blob');
  return new File([blob], docsPdfFileName(docsOutput), { type: 'application/pdf' });
};

// Transaction IDs already uploaded (or uploading) in this browser session, so
// remounts of the output page don't upload the same summary again.
const uploadedTransactionIds = new Set();

// Upload the docs summary PDF to S3 so the Past Transactions zip can include
// it. Runs as soon as an output page shows the documents, not only when the
// user clicks Download. Safe to call repeatedly: it uploads each transaction
// once, and a failed upload is retried on the next call. Failures are logged
// only - the user still has the documents on screen.
export const uploadDocsSummaryToS3 = async (docsFormData, docsOutput, pdfFile = null) => {
  const transactionId = docsOutput?.["TransactionID"];
  if (!hasResponse(docsOutput) || !transactionId || uploadedTransactionIds.has(transactionId)) {
    return;
  }
  uploadedTransactionIds.add(transactionId);

  try {
    const pdfBlob = pdfFile || (await buildDocsSummaryPdf(docsFormData, docsOutput));
    const result = await uploadSummaryPdfToS3({
      pdfBlob,
      fileName: docsPdfFileName(docsOutput),
      // Refi docs IDs end in _REFD, purchase ones in _D.
      callType: transactionId.endsWith('_REFD') ? 'Refi-Docs' : 'Docs',
      vin: vinForUpload(docsFormData?.vinCode, transactionId),
    });
    console.log('Docs summary uploaded to S3:', result?.key);
  } catch (error) {
    uploadedTransactionIds.delete(transactionId);
    console.error('Docs summary upload to S3 failed:', error);
  }
};
